/**
 * The wardrobe (`docs/rpg.md` §19, §31–32).
 *
 * Twelve cosmetics and two honorifics, and every one of them is exactly that: a
 * drawing and a name. Nothing in the codebase reads equipment for difficulty,
 * XP, recommendations or access — `packages/world/src/items.ts` opens with that
 * rule and this file is the data it governs.
 *
 * The catalogue is written in two halves, and the halves say different things.
 *
 * **Six earned pieces** are the ones that matter. §19 gives three of them by
 * name — Master Algebra for the Algebra Staff, Master Geometry for the Geometry
 * Shield, Master Calculus for the Infinity Staff — and they are the only items
 * here condition-ed on mastery, which is the strictest thing the platform
 * measures: earned by practice, six attempts minimum, never credited by the
 * placement test. A staff handed out for a diagnostic answer would be the
 * pay-to-win shortcut §19 spends its last four lines warning about, in the one
 * currency that matters on this platform. The other three are deeds — two boss
 * capes and a circlet for ten finished quests.
 *
 * **Six shop pieces**, 30–120 gold, exist so that the gold quests and bosses
 * already pay has somewhere to go. They are deliberately the *plainer* half of
 * the catalogue: a sun hat, a compass, an abacus. The best-looking things in
 * the game are the ones you learn your way into, and if the shop held the
 * showpieces then gold — which is a count of how much a student has done —
 * would quietly become the thing worth chasing (§47).
 *
 * Two things this file is not allowed to grow. There is no random item and no
 * chest to open one out of (§44), and there is no item purchasable with money;
 * a cosmetic behind a card number puts a visible mark of whose family paid on a
 * child's avatar, and the paywall in this platform sells the volume of
 * generated practice and nothing else.
 *
 * Ids are permanent. A student's `rpg.inventory` holds them forever, so
 * renaming one takes an item off somebody who earned it.
 */

import type { ItemDef, TitleDef } from '@yuan-math/world';

/* -------------------------------------------------------------------------- */
/* Earned — §19's signature pieces                                             */
/* -------------------------------------------------------------------------- */

/**
 * The three staffs and shields §19 names, each on its strand's mastery.
 *
 * The counts are pitched at "a real stretch of that mathematics, not the whole
 * of it": ten algebra concepts is most of a first algebra course, ten geometry
 * concepts spans measurement through proof, and eight calculus concepts is the
 * limit–derivative–integral spine. Asking for a whole strand would make the
 * Infinity Staff a thing three students ever see, and §32 asks achievements to
 * reward meaningful learning rather than exhaustiveness.
 */
const SIGNATURE: ItemDef[] = [
  {
    id: 'item-algebra-staff',
    name: 'Algebra Staff',
    emoji: '🔮',
    slot: 'held',
    flavor: 'Cut from Equation Forest. The head of it balances, which is the whole trick.',
    art: 'algebra-staff',
    source: { kind: 'earned', condition: { kind: 'master-concepts-in-strand', strandId: 'algebra', count: 10 } },
  },
  {
    id: 'item-geometry-shield',
    name: 'Geometry Shield',
    emoji: '📐',
    slot: 'held',
    flavor: 'Every angle on its face was proved before it was forged.',
    art: 'geometry-shield',
    source: { kind: 'earned', condition: { kind: 'master-concepts-in-strand', strandId: 'geometry', count: 10 } },
  },
  {
    id: 'item-infinity-staff',
    name: 'Infinity Staff',
    emoji: '♾️',
    slot: 'held',
    flavor: 'It never quite arrives at its own tip, and gets arbitrarily close.',
    art: 'infinity-staff',
    source: { kind: 'earned', condition: { kind: 'master-concepts-in-strand', strandId: 'calculus', count: 8 } },
  },
];

/* -------------------------------------------------------------------------- */
/* Earned — deeds                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A cape per early boss, and a circlet for a stretch of story.
 *
 * Two capes for the eight bosses the map now has: PR 13 wrote six more fights
 * and stopped at the fights, so their trophies are still to come and belong
 * with the next pass over the wardrobe rather than tacked onto a content pass.
 * `validateItems` refuses a condition naming a boss nobody defined, which is
 * what keeps that honest — an unearnable trophy looks exactly like an unearned
 * one on the tile, and would sit there forever.
 */
const TROPHIES: ItemDef[] = [
  {
    id: 'item-gatekeepers-cape',
    name: "Gatekeeper's Cape",
    emoji: '🧣',
    slot: 'cape',
    flavor: 'Serla keeps one for everyone who can hold a ledger against her. There are not many.',
    art: 'gatekeeper-cape',
    source: { kind: 'earned', condition: { kind: 'clear-boss', bossId: 'boss-starter-village' } },
  },
  {
    id: 'item-dragonscale-cape',
    name: 'Dragonscale Cape',
    emoji: '🐲',
    slot: 'cape',
    flavor: 'Shed scales, not taken ones. The Number Dragon is a poor loser but a fair one.',
    art: 'dragonscale-cape',
    source: { kind: 'earned', condition: { kind: 'clear-boss', bossId: 'boss-number-kingdom' } },
  },
  {
    id: 'item-wayfarers-circlet',
    name: "Wayfarer's Circlet",
    emoji: '🎖️',
    slot: 'headgear',
    flavor: 'Ten errands run to the end. The villages notice that sort of thing.',
    art: 'circlet',
    source: { kind: 'earned', condition: { kind: 'claim-quests', count: 10 } },
  },
];

/* -------------------------------------------------------------------------- */
/* Shop                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Six modest pieces, 30–120 gold, priced against what the world pays.
 *
 * A main quest pays 10–30 gold and a boss 40–80, so the sun hat is about two
 * errands and the Starlit Mantle is a kingdom's whole story. Nothing here is
 * cheap enough to be pointless or dear enough to be a term's homework
 * (`ITEM_GOLD_MIN`/`MAX` enforce both ends, and the reasoning is there).
 */
const SHOP: ItemDef[] = [
  {
    id: 'item-woven-sun-hat',
    name: 'Woven Sun Hat',
    emoji: '👒',
    slot: 'headgear',
    flavor: 'The meadow gets hot by ten. Everyone in Counting Meadow owns one.',
    art: 'sun-hat',
    source: { kind: 'shop', gold: 30 },
  },
  {
    id: 'item-travellers-cloak',
    name: "Traveller's Cloak",
    emoji: '🧥',
    slot: 'cape',
    flavor: 'Oiled wool, four pockets, and a hem that has seen the road out of the village.',
    art: 'traveller-cape',
    source: { kind: 'shop', gold: 40 },
  },
  {
    id: 'item-merchants-abacus',
    name: "Merchant's Abacus",
    emoji: '🧮',
    slot: 'held',
    flavor: 'Beads worn smooth. It does no arithmetic you cannot already do.',
    art: 'abacus',
    source: { kind: 'shop', gold: 55 },
  },
  {
    id: 'item-scholars-cap',
    name: "Scholar's Cap",
    emoji: '🎓',
    slot: 'headgear',
    flavor: 'Worn flat by the Foundry lecturers, and at an angle by everyone else.',
    art: 'scholar-cap',
    source: { kind: 'shop', gold: 70 },
  },
  {
    id: 'item-brass-compass',
    name: 'Brass Compass',
    emoji: '🧭',
    slot: 'held',
    flavor: 'Points at the harbour, which is north, which is usually where you want to be.',
    art: 'compass',
    source: { kind: 'shop', gold: 90 },
  },
  {
    id: 'item-starlit-mantle',
    name: 'Starlit Mantle',
    emoji: '✨',
    slot: 'cape',
    flavor: 'The Observatory dyes them once a year, and will not say how.',
    art: 'starlit-cape',
    source: { kind: 'shop', gold: 120 },
  },
];

/**
 * What is out on the offshore isles (`isles.ts`).
 *
 * Conditioned on the same achievement that opens the isle it sits on, rather
 * than on the isle itself. The isle is where the reward is *presented*; the
 * achievement is what grants it — one entitlement path, the way a quest page
 * presents work that `recordAttempt` is what credits. A student who never walks
 * the causeway still owns the hat, which is the same rule quests already follow.
 *
 * Each has its own drawing, because `validateItems` insists art is unique — two
 * items sharing a picture are two tiles a student cannot tell apart in the
 * wardrobe, which is the whole of what a cosmetic is for.
 */
const ISLAND: ItemDef[] = [
  {
    id: 'item-tidewalkers-hat',
    name: "Tidewalker's Hat",
    emoji: '👒',
    slot: 'headgear',
    flavor: 'Salt-stiffened brim. The sandbar is only there for six hours a day and this has been out on it.',
    art: 'tide-hat',
    source: { kind: 'earned', condition: { kind: 'earn-achievement', achievementId: 'first-mastery' } },
  },
  {
    id: 'item-keepers-lamp',
    name: "Keeper's Lamp",
    emoji: '🏮',
    slot: 'held',
    flavor: 'Trimmed every evening by whoever walked out last. It has not gone out in a long time.',
    art: 'keepers-lamp',
    source: { kind: 'earned', condition: { kind: 'earn-achievement', achievementId: 'streak-7' } },
  },
  {
    id: 'item-tally-cloak',
    name: 'Tally Cloak',
    emoji: '🧾',
    slot: 'cape',
    flavor: 'The traders on the bank mark the hem rather than a ledger. Every mark on this one is right.',
    art: 'tally-cape',
    source: { kind: 'earned', condition: { kind: 'earn-achievement', achievementId: 'precision' } },
  },
  {
    id: 'item-north-parka',
    name: 'North Parka',
    emoji: '🧥',
    slot: 'cape',
    flavor: 'Lined, hooded and far too warm for anywhere else on the map. Twenty-five tiles of road went into earning it.',
    art: 'north-parka',
    source: { kind: 'earned', condition: { kind: 'earn-achievement', achievementId: 'twenty-mastered' } },
  },
  {
    id: 'item-sun-shawl',
    name: 'Sun Shawl',
    emoji: '🧣',
    slot: 'cape',
    flavor: 'Loose, pale and thrown over the shoulders against the glare. Thirty tiles of road went into earning it.',
    art: 'sun-shawl',
    source: { kind: 'earned', condition: { kind: 'earn-achievement', achievementId: 'wide-reader' } },
  },
];

/** Everything a student can wear, earned pieces first. */
export const ITEMS: ItemDef[] = [...SIGNATURE, ...TROPHIES, ...ISLAND, ...SHOP];

/* -------------------------------------------------------------------------- */
/* Titles                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Honorifics, from deeds only.
 *
 * Deeds rather than counts, deliberately: a boss goes down once and stays down,
 * so a title from one is never taken back. A title conditioned on mastery would
 * be revoked by a fortnight away from the platform, and a name a student loses
 * for being ill is a punishment dressed as a reward (§44).
 *
 * These sit beside the level ladder in `deriveTitle`, which is XP and habit.
 * Neither is a claim about mathematics, and mastery is shown above both.
 */
export const TITLES: TitleDef[] = [
  {
    id: 'title-gate-warden',
    text: 'Gate Warden',
    condition: { kind: 'clear-boss', bossId: 'boss-starter-village' },
  },
  {
    id: 'title-dragonsbane',
    text: 'Dragonsbane',
    condition: { kind: 'clear-boss', bossId: 'boss-number-kingdom' },
  },
  /*
   * The two isle titles. Achievements rather than bosses, and safe for the same
   * reason a cleared boss is: an earned achievement is never revoked, so
   * neither of these can be taken off a student by a fortnight away from the
   * platform. That was the whole objection to a title conditioned on mastery.
   */
  {
    id: 'title-lightkeeper',
    text: 'Lightkeeper',
    condition: { kind: 'earn-achievement', achievementId: 'streak-7' },
  },
  {
    id: 'title-far-walker',
    text: 'Far Walker',
    condition: { kind: 'earn-achievement', achievementId: 'comeback' },
  },
  {
    id: 'title-northwalker',
    text: 'Northwalker',
    condition: { kind: 'earn-achievement', achievementId: 'twenty-mastered' },
  },
  {
    id: 'title-duneswalker',
    text: 'Duneswalker',
    condition: { kind: 'earn-achievement', achievementId: 'wide-reader' },
  },
];
